import { useFormContext, useWatch } from 'react-hook-form';
import { useEffect, useMemo, useState } from 'react';
import {
  CurrencyInputV2,
  DocumentNumberFieldV2,
  FormFieldV2,
  NumericInputV2,
  SearchFieldV2 as SearchField,
  AritoIcon
} from '@/components/custom/erp';
import {
  accountSearchColumns,
  hanThanhToanSearchColumns,
  khachHangSearchColumns,
  MA_CHUNG_TU,
  nhanVienSearchColumns,
  QUERY_KEYS
} from '@/constants';
import { AccountModel, HanThanhToan, KhachHang, NhanVien } from '@/types/schemas';
import { useDetailSelection } from '@/hooks/use-detail-selection';
import { FormFieldActions, FormFieldState } from '../../hooks';
import { useHoaDonBanHangSearchContext } from '../../context';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { FormMode } from '@/types/form';

interface BasicInfoTabProps {
  formMode: FormMode;
  formState: {
    state: FormFieldState;
    actions: FormFieldActions;
  };
}

const tabsMini = [
  { id: 0, title: 'Chứng từ bán' },
  { id: 1, title: 'Hóa đơn' },
  { id: 2, title: 'Phiếu xuất' }
];

const topCheckboxClassName = 'flex h-8 items-center gap-2';
const topSelectClassName =
  'h-8 rounded-none border-0 border-b border-b-gray-200 bg-transparent px-2 text-[13px] font-semibold text-black outline-none focus:border-b-blue-600 disabled:opacity-100';
const topNumberInputClassName =
  'h-8 rounded-none border-0 border-b border-b-gray-200 bg-transparent px-2 text-right text-[13px] font-semibold text-black outline-none focus:border-b-blue-600 disabled:opacity-100';

export function BasicInfoTab({ formMode, formState: { state, actions } }: BasicInfoTabProps) {
  const [selectedTabId, setSelectedTabId] = useState<number>(0);
  const { setValue } = useFormContext();
  const { setNgayCt, setCustomerGroupUuid } = useHoaDonBanHangSearchContext();
  const handleAccountSelection = useDetailSelection<AccountModel>({
    endpoint: QUERY_KEYS.TAI_KHOAN,
    onSelect: actions.setTaiKhoan
  });
  const ngay_ct = useWatch({ name: 'ngay_ct' });

  useEffect(() => {
    if (ngay_ct) {
      setValue('ngay_px', ngay_ct);
      if (formMode !== 'view') setNgayCt(ngay_ct);
    }
  }, [ngay_ct, setValue, formMode, setNgayCt]);

  useEffect(() => {
    if (formMode === 'view') return;
    const customerGroupUuid = state.khachHang?.customer_group1;
    setCustomerGroupUuid(customerGroupUuid || undefined);
  }, [formMode, state.khachHang, setCustomerGroupUuid]);

  const pt_tao_yn = state.pt_tao_yn;
  const ck_yn = state.ck_yn;
  const loai_ck = state.loai_ck;
  const px_yn = state.px_yn;
  const availableTabs = useMemo(() => tabsMini.filter(tab => (tab.id === 2 ? px_yn : true)), [px_yn]);

  useEffect(() => {
    const isCurrentTabAvailable = availableTabs.some(tab => tab.id === selectedTabId);
    if (!isCurrentTabAvailable) {
      setSelectedTabId(0);
    }
  }, [availableTabs, selectedTabId]);

  const rowClassName = 'flex items-center gap-2 xl:gap-3';
  const leftLabelClassName = 'w-40 shrink-0 text-[13px]';
  const middleLabelClassName = 'w-[122px] shrink-0 text-[13px] xl:w-[132px]';
  const rightLabelClassName = 'w-[92px] shrink-0 text-[13px] xl:w-[104px] 2xl:w-28';
  const disabled = formMode === 'view';

  return (
    <div className='overflow-x-auto'>
      <div className='min-w-[1400px] px-3 pb-2 pt-1.5 xl:min-w-0 xl:px-4 xl:pb-3 xl:pt-2'>
        <div className='mb-1.5 flex min-h-8 items-center gap-7'>
          <div className={topCheckboxClassName}>
            <Checkbox
              name='hdbh_yn'
              id='hdbh_yn'
              checked={state.hdbh_yn}
              onCheckedChange={checked => actions.setHdbhYn(checked === true)}
              disabled={disabled}
            />
            <Label htmlFor='hdbh_yn' className='whitespace-nowrap text-[14px]'>
              Hóa đơn
            </Label>
          </div>

          <div className={topCheckboxClassName}>
            <Checkbox
              name='px_yn'
              id='px_yn'
              checked={state.px_yn}
              onCheckedChange={checked => actions.setPxYn(checked === true)}
              disabled={disabled}
            />
            <Label htmlFor='px_yn' className='whitespace-nowrap text-[14px]'>
              Xuất kho
            </Label>
          </div>

          <div className={topCheckboxClassName}>
            <Checkbox
              name='pt_tao_yn'
              id='pt_tao_yn'
              checked={pt_tao_yn}
              onCheckedChange={checked => actions.setPtTaoYn(checked === true)}
              disabled={disabled}
            />
            <Label htmlFor='pt_tao_yn' className={pt_tao_yn ? 'sr-only' : 'whitespace-nowrap text-[14px]'}>
              Thu tiền
            </Label>
            <select
              name='ma_httt'
              value={state.ma_httt}
              disabled={disabled || !pt_tao_yn}
              aria-hidden={!pt_tao_yn}
              tabIndex={pt_tao_yn ? 0 : -1}
              onChange={event => {
                setValue('ma_httt', event.target.value);
                actions.setMaHttt(event.target.value);
              }}
              className={`${topSelectClassName} w-[150px] ${pt_tao_yn ? '' : 'hidden'}`}
            >
              <option value='TMB'>Tiền mặt</option>
              <option value='CKB'>Chuyển khoản</option>
              <option value='KB'>Khác</option>
            </select>
          </div>

          <div className={topCheckboxClassName}>
            <Checkbox
              name='ck_yn'
              id='ck_yn'
              checked={ck_yn}
              onCheckedChange={checked => actions.setCkYn(checked === true)}
              disabled={disabled}
            />
            <Label htmlFor='ck_yn' className={ck_yn ? 'sr-only' : 'whitespace-nowrap text-[14px]'}>
              Chiết khấu
            </Label>
            {ck_yn && (
              <>
                <select
                  name='loai_ck'
                  value={state.loai_ck}
                  disabled={disabled}
                  onChange={event => {
                    setValue('loai_ck', event.target.value);
                    actions.setLoaiCk(event.target.value);
                  }}
                  className={`${topSelectClassName} w-[250px]`}
                >
                  <option value='1'>Chiết khấu tự nhập chi tiết</option>
                  <option value='2'>Chiết khấu % tổng đơn</option>
                  <option value='3'>Giảm tiền trên tổng hóa đơn</option>
                </select>
                {String(loai_ck) !== '1' && (
                  <NumericInputV2
                    name='ck_tl_nt'
                    disabled={disabled}
                    value={state.ck_tl_nt}
                    onValueChange={nextValue => {
                      setValue('ck_tl_nt', nextValue);
                      actions.setCkTlNt(nextValue);
                    }}
                    className={`${topNumberInputClassName} w-[150px]`}
                  />
                )}
              </>
            )}
          </div>
        </div>

        <div className='grid grid-cols-12 gap-4 xl:gap-6'>
          <div className='col-span-6 min-w-0 space-y-0'>
            <div className={rowClassName}>
              <Label className={leftLabelClassName}>Mã khách hàng</Label>
              <div className='flex min-w-0 flex-1 items-center gap-3'>
                <div className='w-[180px] xl:w-[200px]'>
                  <SearchField<KhachHang>
                    type='text'
                    name='ma_kh'
                    errorDisplay='tooltip'
                    value={state.khachHang?.customer_code || ''}
                    searchEndpoint={`/${QUERY_KEYS.KHACH_HANG}/`}
                    searchColumns={khachHangSearchColumns}
                    onRowSelection={actions.setKhachHang}
                    columnDisplay='customer_code'
                    dialogTitle='Danh mục khách hàng'
                    className='w-full'
                    disabled={formMode === 'view'}
                  />
                </div>

                <div className='flex min-w-0 flex-1 items-center gap-2'>
                  <Label className='w-20 shrink-0 text-[13px]'>Mã số thuế</Label>
                  <div className='min-w-0 max-w-[220px] flex-1'>
                    <FormFieldV2
                      name='ma_so_thue'
                      type='text'
                      disabled={formMode === 'view'}
                      placeholder='Nhập và tra cứu'
                      value={state.khachHang?.tax_code || undefined}
                    />
                  </div>
                  <button type='button' className='flex items-center justify-center border border-gray-200 px-3 py-1.5'>
                    <AritoIcon icon={15} className='shrink-0' />
                  </button>
                  <button type='button' className='flex items-center justify-center border border-gray-200 px-3 py-1.5'>
                    <AritoIcon icon={888} />
                  </button>
                </div>
              </div>
            </div>

            <div className={rowClassName}>
              <Label className={leftLabelClassName}>Tên khách hàng/Ng.mua</Label>
              <div className='min-w-0 flex-1'>
                <FormFieldV2
                  name='ten_kh_thue'
                  type='text'
                  disabled={formMode === 'view'}
                  value={state.khachHang?.customer_name || undefined}
                  placeholder='Nhập tên khách hàng/đơn vị'
                />
              </div>
            </div>

            <div className={rowClassName}>
              <Label className={leftLabelClassName}>Địa chỉ/Email</Label>
              <div className='min-w-0 flex-1'>
                <FormFieldV2
                  name='dia_chi'
                  type='text'
                  disabled={formMode === 'view'}
                  value={state.khachHang?.address || undefined}
                  placeholder='Nhập địa chỉ khách hàng'
                />
              </div>
            </div>

            <div className={rowClassName}>
              <Label className={leftLabelClassName}>Mã nhân viên</Label>
              <SearchField<NhanVien>
                type='text'
                name='ma_nvbh'
                value={state.nhanVien?.ma_nhan_vien || state.khachHang?.sales_rep_data?.ma_nhan_vien || ''}
                relatedFieldValue={
                  state.nhanVien?.ho_ten_nhan_vien || state.khachHang?.sales_rep_data?.ho_ten_nhan_vien || ''
                }
                searchEndpoint={`/${QUERY_KEYS.NHAN_VIEN}/`}
                columnDisplay='ma_nhan_vien'
                displayRelatedField='ho_ten_nhan_vien'
                searchColumns={nhanVienSearchColumns}
                dialogTitle='Danh sách nhân viên bán hàng'
                className='w-100'
                onRowSelection={actions.setNhanVien}
                disabled={formMode === 'view'}
              />
            </div>

            <div className={rowClassName}>
              <Label className={leftLabelClassName}>Tài khoản nợ</Label>
              <SearchField<AccountModel>
                type='text'
                name='tk'
                errorDisplay='tooltip'
                value={state.taiKhoan?.code || state.khachHang?.account_data?.code || ''}
                relatedFieldValue={state.taiKhoan?.name || state.khachHang?.account_data?.name || ''}
                searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
                columnDisplay='code'
                displayRelatedField='name'
                searchColumns={accountSearchColumns}
                dialogTitle='Danh sách tài khoản'
                className='w-100'
                onRowSelection={handleAccountSelection}
                disabled={formMode === 'view'}
              />
            </div>

            <div className={rowClassName}>
              <Label className={leftLabelClassName}>Diễn giải</Label>
              <div className='min-w-0 flex-1'>
                <FormFieldV2 name='dien_giai' type='text' disabled={formMode === 'view'} />
              </div>
            </div>
          </div>

          <div className='col-span-3 min-w-0 space-y-1'>
            <div className={rowClassName}>
              <FormFieldV2
                name='du_cn_thu'
                label='Dư công nợ'
                labelClassName={middleLabelClassName}
                fieldClassName='w-full text-red-600'
                className='w-[220px]'
                value={0}
                disabled
              />
            </div>

            <div className={rowClassName}>
              <FormFieldV2
                name='ong_ba'
                label='Người nhận'
                labelClassName={middleLabelClassName}
                fieldClassName='w-full'
                disabled={formMode === 'view'}
                className='w-[220px]'
                value={state.khachHang?.contact_person || undefined}
                placeholder='Nhập tên người nhận'
              />
            </div>

            <div className={rowClassName}>
              <FormFieldV2
                name='e_mail'
                label='Email'
                labelClassName={middleLabelClassName}
                fieldClassName='w-full'
                className='w-[220px]'
                disabled={formMode === 'view'}
                value={state.khachHang?.email || undefined}
                placeholder='Nhập email'
              />
            </div>

            {!pt_tao_yn && (
              <div className={rowClassName}>
                <Label className={middleLabelClassName}>Hạn thanh toán</Label>
                <div className='w-[220px]'>
                  <SearchField<HanThanhToan>
                    type='text'
                    name='ma_tt'
                    value={state.hanThanhToan?.ma_tt || state.khachHang?.payment_term_data?.ma_tt || ''}
                    searchEndpoint={`/${QUERY_KEYS.HAN_THANH_TOAN}/`}
                    columnDisplay='ma_tt'
                    searchColumns={hanThanhToanSearchColumns}
                    dialogTitle='Danh mục hạn thanh toán'
                    className='w-full'
                    onRowSelection={actions.setHanThanhToan}
                    disabled={formMode === 'view'}
                  />
                </div>
              </div>
            )}
          </div>

          <div className='col-span-3 flex min-w-0 items-stretch'>
            <div className='min-w-0 flex-1 space-y-1 pr-3'>
              {selectedTabId === 0 && (
                <div role='tabpanel' id='hoa-don-ban-hang-mini-panel-0' aria-labelledby='hoa-don-ban-hang-mini-tab-0'>
                  <DocumentNumberFieldV2
                    ma_ct={MA_CHUNG_TU.BAN_HANG.HOA_DON_BAN_HANG}
                    name='so_ct'
                    onQuyenChungTuChange={actions.setQuyenChungTu}
                    quyenChungTu={state.quyenChungTu}
                    soChungTu={state.soChungTu}
                    onSoChungTuChange={actions.setSoChungTu}
                    disabled={formMode === 'view'}
                    formMode={formMode}
                    className='flex items-center gap-3'
                    labelClassName={rightLabelClassName}
                    classNameSearchField='min-w-0 flex-1'
                  />

                  <FormFieldV2
                    label='Ngày hạch toán'
                    name='ngay_ct'
                    type='date'
                    disabled={formMode === 'view'}
                    className='w-full'
                    labelClassName={rightLabelClassName}
                  />
                </div>
              )}

              {selectedTabId === 1 && (
                <div role='tabpanel' id='hoa-don-ban-hang-mini-panel-1' aria-labelledby='hoa-don-ban-hang-mini-tab-1'>
                  <FormFieldV2
                    label='Số hóa đơn'
                    name='so_ct_hddt0'
                    disabled={formMode === 'view'}
                    className='w-full'
                    labelClassName={rightLabelClassName}
                  />

                  <FormFieldV2
                    label='Ngày hóa đơn'
                    name='ngay_ct_hddt0'
                    type='date'
                    disabled={formMode === 'view'}
                    className='w-full'
                    labelClassName={rightLabelClassName}
                  />

                  <FormFieldV2
                    label='Ký hiệu'
                    name='so_ct2_hddt'
                    type='text'
                    disabled={formMode === 'view'}
                    className='w-full'
                    labelClassName={rightLabelClassName}
                  />
                </div>
              )}

              {px_yn && selectedTabId === 2 && (
                <div role='tabpanel' id='hoa-don-ban-hang-mini-panel-2' aria-labelledby='hoa-don-ban-hang-mini-tab-2'>
                  <DocumentNumberFieldV2
                    ma_ct={MA_CHUNG_TU.TON_KHO.PHIEU_XUAT_KHO}
                    name='so_px'
                    onQuyenChungTuChange={actions.setQuyenPhieuXuat}
                    quyenChungTu={state.quyenPhieuXuat}
                    soChungTu={state.soPhieuXuat}
                    onSoChungTuChange={actions.setSoPhieuXuat}
                    disabled={formMode === 'view'}
                    className='flex items-center gap-3'
                    labelClassName={rightLabelClassName}
                    classNameSearchField='min-w-0 flex-1'
                    formMode={formMode}
                  />

                  <FormFieldV2
                    label='Ngày xuất kho'
                    name='ngay_px'
                    type='date'
                    disabled={formMode === 'view'}
                    className='w-full'
                    labelClassName={rightLabelClassName}
                  />
                </div>
              )}

              <CurrencyInputV2
                formMode={formMode}
                className='flex items-center gap-3'
                labelClassName={rightLabelClassName}
                classNameInput='min-w-0 flex-1'
              />

              <FormFieldV2
                name='status'
                type='select'
                label='Trạng thái'
                disabled={formMode === 'view'}
                className='w-full'
                labelClassName={rightLabelClassName}
                fieldClassName='w-full'
                options={[
                  { value: '0', label: 'Lập chứng từ' },
                  { value: '3', label: 'Chờ duyệt' },
                  { value: '5', label: 'Xuất hóa đơn' },
                  { value: '7', label: 'Bỏ duyệt đơn hàng' },
                  { value: '9', label: 'Hủy' }
                ]}
              />

              <div className='mt-1 flex'>
                <div className='h-2 shrink-0 xl:w-[104px] 2xl:w-32' />
                <FormFieldV2
                  label='Dữ liệu nhận được'
                  name='transfer_yn'
                  type='checkbox'
                  className='w-full'
                  labelClassName='w-32 text-[13px]'
                  value={false}
                />
              </div>
            </div>

            <div
              role='tablist'
              aria-orientation='vertical'
              className='-mt-6 flex w-10 shrink-0 flex-col items-end gap-0.5 self-start'
            >
              {availableTabs.map(tab => (
                <button
                  key={tab.id}
                  type='button'
                  role='tab'
                  id={`hoa-don-ban-hang-mini-tab-${tab.id}`}
                  aria-selected={selectedTabId === tab.id}
                  aria-controls={`hoa-don-ban-hang-mini-panel-${tab.id}`}
                  onClick={event => {
                    event.preventDefault();
                    event.stopPropagation();
                    setSelectedTabId(tab.id);
                  }}
                  className={`flex min-h-[64px] w-5 items-center justify-center rounded-sm px-1 py-2 text-center text-[10px] font-bold text-white outline-none [writing-mode:vertical-rl] ${
                    selectedTabId === tab.id ? 'bg-main/80' : 'bg-gray-500 hover:bg-gray-600'
                  }`}
                >
                  {tab.title}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
